'use client';

import { sendCartNotification } from '@/features/cart/services/cartService';
import { useChatStore } from '@/features/chat/stores/chatStore';
import { Button } from '@/shared/components/atoms/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/atoms/Popover';
import logger from '@/utils/logger';
import { ReactNode, useState } from 'react';
import { Product, Seller } from '../../types/models/product';

interface SellerChatModalProps {
  isOpen: boolean;
  isCartProduct?: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  onChatStarted?: () => void;
  product?: Product;
  isShowPhoneNumber?: boolean;
}

export function SellerChatModal({ isOpen, isCartProduct, onOpenChange, children, onChatStarted, product, isShowPhoneNumber }: SellerChatModalProps) {
  const [isStartingChat, setIsStartingChat] = useState(false);
  const { startChat, toggleOverlay, setActiveProductData } = useChatStore();

  const handleStartChat = async (seller: Seller) => {
    try {
      setIsStartingChat(true);

      const chatProduct = product
        ? ({ ...product, id: isCartProduct ? -1 : product.id, productId: isCartProduct ? -1 : product.id } as Product)
        : undefined;

      await startChat(seller.email, chatProduct, seller);

      toggleOverlay();
      setActiveProductData(product || null);

      logger.info('New chat started');

      onOpenChange(false);

      onChatStarted?.();

      try {
        await sendCartNotification(seller.email);
      } catch (notificationError) {
        logger.error('Failed to send cart notification:', notificationError);
      }
    } catch (error) {
      logger.error('Failed to start chat:', error);
    } finally {
      setIsStartingChat(false);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4 mt-2" align="start">
        <div className="mb-4">
          <h3 className="font-medium text-lg">연락처 선택</h3>
        </div>
        <div className="space-y-3 max-h-60 overflow-y-auto">
          {product?.sellers.map((seller, index) => (
            <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <div className="font-medium">{seller.company}</div>
                <div className="text-sm text-gray-600">{seller.name}</div>
                {isShowPhoneNumber && <div className="text-sm text-gray-500">{seller.phone_number}</div>}
              </div>
              <Button
                onClick={() => handleStartChat(seller)}
                disabled={isStartingChat}
                size="sm"
              >
                대화하기
              </Button>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
